/* eslint-disable sonarjs/no-nested-functions */
import { connectStoreEffects, StateCreatorWithDevtools, StoreEffect } from '../store-utils';
import { PackageInfoModel } from '../../mvcapi/models/common/package-info-model';
import { AddToCartStoreResult, useAddTicketsToCartStore } from '../add-tickets-to-cart-store';
import { addPackageToCartNewSale, clearUnselectedTickets, getPackageInfo, unselectSpecificTickets } from '../../mvcapi/buy-ticket-api';
import { concat, forEach, map, remove, some, uniqueId } from 'lodash';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { create } from 'zustand';
import { AddToCartItem, AddToCartItemType, AddToCartPackageDateItem, AddToCartPackageItem, areCartItemsComplete, EventDateCartItem, findItemById, findItemsByTicketIds, findTicketInItemsByTicketId, getAddToCartItemByPackageId, getAllEventDateItemsFromItems, getCartPackageItemsFromTicketModels, getTicketIdsFromItems, getTicketsFromItem, getTicketsFromItems } from './models/add-to-cart-items';
import { AddToCartPackageRequest, PriceQuantity } from '../../mvcapi/models/buyticket/ticketselection';
import { getExpiredTicketsInWarningReport } from '../../logic';
import { getSelectedLastPriceGroup, getTicketPriceSingleSelectionPanel } from '../../mvcapi/ticket-price';
import { createAddToCartPackageRequest } from '../../logic/sales-util';
import { PriceChangeSuggestion, TicketPriceSingleSelectionPanel } from '../../mvcapi/models/buyticket';
import { TicketModel } from '../../mvcapi/models/buyticket/submodels';
import { SelectedTicketsByEventDateId } from '../../logic/models';
import { produce } from 'immer';

// INFO: New sale

export type PackageDateItemInfo = {
    eventDateId: number,
    parent: AddToCartPackageItem,
    items: AddToCartPackageDateItem,
}

export interface AddPackageToCartStoreExt {

    /** Error that could happen during loading process of programs, event, event dates, ... */
    loadingPackage: boolean,
    packageId: number,
    /** Je crois qu'on ne devrait jamais appeler le set ID directement mais les promos semble faire une passe d'ID sans loader le package */
    setPackageId: (packageId: number) => void,
    package: PackageInfoModel,
    isAPackageSale: () => boolean,

    fixedSeat: () => boolean,
    fixedPackage: () => boolean,

    loadPackage: (packageId: number) => Promise<void>,

    /** Détermine et choisi le main event date du forfait après que l'utilisateur ait faire sa sélection d'événement  */
    _changeMainEventDate: (packageInfo: PackageInfoModel) => void,

    /** Utiliser après avoir trouvé des billets pour tous les événements du forfait */
    addNewTickets: (foundTickets: TicketModel[]) => Promise<boolean>,

    /** Utiliser pour récupérer le panel de liste de prix */
    pricePanel: TicketPriceSingleSelectionPanel,
    fetchingPricePanel: boolean,
    refreshPricesListPanel: (addToCartItem: AddToCartPackageItem[]) => Promise<void>,

    /** List of the selected event date IDs in the package. (Where the user can choose its events) */
    selectedEventDateIds: number[],
    setSelectedEventDateIds: (selectedEventDateIds: number[]) => void,

    /** The selected event date id for display */
    selectedEventDateIdForDisplay: number,
    setSelectedEventDateIdForDisplay: (eventDateId: number) => void,

    /** Call that when the user has selected the price of the tickets */
    _runPriceSelectionForNewItems: (addToCartItems: AddToCartItem[]) => Promise<boolean>,

    /** Call this for item modification */
    startItemModification: (itemId: string) => void,

    /** 
     * Remove one or more package item using one of their ticket id. This is usefull
     * for the venue plan that works with simple ticket ids. 
     */
    startItemRemovalByTicketIdsProcessWithConfirm: (ticketIds: number[]) => void,

    // Selected tickets management =========================================
    /** The current selection of the user */
    packageItems: AddToCartPackageItem[],

    /** @deprecated Use the items instead. (This is now and temporary a calculated field) */
    selectedTicketsByEventDateId: SelectedTicketsByEventDateId, __recalculateSelectedTicketsByEventDateId: StoreEffect<any>, // CALCULATED FIELD

    /** @Computed list of items by event date Ids */
    subItemsByEventDateId: { [eventDateId: string]: PackageDateItemInfo[] },
    removeTickets: (ticketIdsToRemove: number[], unselect: boolean) => Promise<boolean>,    /** Call that when new tickets are selected in the database. This will show the price popup. */
    clearAllTickets: (withUnselect: boolean) => void,

    addPackageToCart: () => Promise<AddToCartStoreResult>,
    getAddToCartRequest: () => AddToCartPackageRequest,

    /* When the TicketSelectionSlice has done its job */
    handleItemsSelected: (associatedItemsWithPrices: AddToCartItem[], isUpdate?: boolean) => void;
    confirmPriceSelectionCompleted: (addToCartItems: AddToCartItem[], pricesQty: PriceQuantity[]) => void;

    hasSelectedItems: () => boolean;
    removeAllTickets: () => void;
    confirmRemoveTicketsProcess: (ticketIdsToRemove: number[]) => void;

    /**
     * Save the selected add to cart items in the state.
     * It also updates seen credit card conditions for every required price.
     * @param associatedItemsWithPrices The items with their selected prices
     * @param isUpdate Determines if the selection is for updating a specific ticket or add new ones
     */
    saveSelectedItems: (associatedItemsWithPrices: AddToCartItem[], isUpdate?: boolean) => void;
    _addSelectedPackageItems: (newPackageItems: AddToCartPackageItem[]) => void;
    _updatePackageItems: (packageId: number, newSelectedItems: AddToCartPackageItem[]) => void;

    // Price List ==========================================================
    /** Apply price change suggestion sent by the server. Like when we add a new membership cards, we want our tickets the use the unlocked price */
    applyPriceChangeSuggestions: (priceChangeSuggestions: PriceChangeSuggestion[]) => void;

    getSelectedSectionNames: () => string[];
}

export const AddPackageToCartStoreExtDef: StateCreatorWithDevtools<AddPackageToCartStoreExt, AddPackageToCartStoreExt> = (set, get) => {
    return {

        // ****************
        // States
        // ****************
        packageId: null,
        setPackageId: (packageId: number) => set({ packageId }, false, "packageId"),

        loadingPackage: false,
        package: null,

        isAPackageSale: () => get().packageId != null,

        /** Sert à savoir si le forfait est en mode siège fixe */
        fixedSeat: () => get().package?.IsFixedSeat,

        /** Sert à savoir si le forfait est en mode forfait fixe (tous les événements du forfaits sont obligatoires) */
        fixedPackage: () => get().package?.IsFixedPackage,

        selectedEventDateIds: [],
        setSelectedEventDateIds: (selectedEventDateIds: number[]) => { 

            // If someone's modifying its events, they're not in the ticket selection anymore. Reset all tickets.
            get().removeAllTickets();

            set({ selectedEventDateIds }, false, "selectedEventDateIds");

            get()._changeMainEventDate(get().package);

        },

        selectedEventDateIdForDisplay: 0,
        setSelectedEventDateIdForDisplay: (selectedEventDateIdForDisplay) => set({ selectedEventDateIdForDisplay }, false, "selectedEventDateIdForDisplay"),
        
        packageItems: [],
        selectedTicketsByEventDateId: {},

        pricePanel: null,
        fetchingPricePanel: false,

        subItemsByEventDateId: {},

        _changeMainEventDate: (packageInfo: PackageInfoModel) => {
            // Find the first event date that has a venue plan and use it as our main event date
            const selectedEventDates = packageInfo.EventDateInfos.filter(e => some(get().selectedEventDateIds, id => id === e.EventDateID));
            const mainEventDate = selectedEventDates.find(e => e.HasVenuePlan);

            if(useAddTicketsToCartStore.getState().eventDateId != mainEventDate?.EventDateID) {
                if(mainEventDate) {
                    get().setSelectedEventDateIdForDisplay(mainEventDate.EventDateID);

                    // On charge les informations de l'événement date choisi pour l'affichage
                    useAddTicketsToCartStore.setState({ eventDate: mainEventDate, eventDateId: mainEventDate.EventDateID }, false, "_changeMainEventDate/set-main-event-date");
                } else {
                    useAddTicketsToCartStore.setState({ eventDate: null, eventDateId: null }, false, "_changeMainEventDate/reset-main-event-date");
                    set({ selectedEventDateIdForDisplay: 0 }, false, "_changeMainEventDate/reset-main-event-date");
                }
            }
        },


        // ****************
        // Actions
        // ****************
        loadPackage: async (packageId: number) => {
            console.log("Loading package ... ", packageId);

            set({ packageId: packageId, loadingPackage: true }, false, "loadPackage/start");


            useAddTicketsToCartStore.setState({ lastSelectedPriceGroup: null}, false, "loadPackage/resetLastSelectedPriceGroup");

            try {
                 const packageInfo = await getPackageInfo(packageId);

                if (packageInfo.IsFixedPackage) { // si c'est un forfait fixe on sélectionne automatiquement tous les événements
                    const selectedEventDateIds = concat(map(packageInfo.MandatoryEventDateInfos, "EventDateID"),
                                                        map(packageInfo.EventDateInfos, "EventDateID"));

                    set({selectedEventDateIds: selectedEventDateIds}, false, "loadPackage/setSelectedEventDateIds");
                    get()._changeMainEventDate(packageInfo);
                }

                // set({ selectedEventDateIdForDisplay: packageInfo.MainEventDateId }, false, "loadPackage/setSelectedEventDateIdForDisplay");

                // On charge les informations de l'événement date choisi pour l'affichage
                // useAddTicketsToCartStore.setState({ 
                //     eventDate: packageInfo.EventDateInfos.find(e => e.EventDateID == packageInfo.MainEventDateId), 
                //     eventDateId: packageInfo.MainEventDateId 
                // }, false, "loadPackage/loadMainEventDate");

                useAddTicketsToCartStore.setState({
                    handleItemsSelectedExternalLogic: { cb: get().handleItemsSelected }
                }, false, "loadPackage/handleItemsSelectedExternalLogic");
                
                set({ package: packageInfo, loadingPackage: false }, false, "loadPackage/success");
            } catch(err) {
                set({ loadingPackage: false }, false, "loadPackage/error");
                useAddTicketsToCartStore.setState({ loadingError: err.message }, false, "loadPackage/error");
            }
        },

        handleItemsSelected: (selectedItemsWithPrice: AddToCartItem[], editMode: boolean = false) => {
            if(editMode) {
                if (selectedItemsWithPrice.length > 0) {
                    let editedItem = selectedItemsWithPrice[0] as AddToCartPackageItem;

                    let packageItems = getAddToCartItemByPackageId(get().packageItems, get().packageId);
    
                    let itemIndex = packageItems.findIndex(item => item._id === editedItem._id);
                    if (itemIndex >= 0) {
                        packageItems = [...packageItems];
                        packageItems[itemIndex] = editedItem;
                        get().saveSelectedItems(packageItems, true);
                    }
                }
    
                // End verison modif
            } else { // New package item with tickets
                // Prendre le premier item de la liste car des package items sont ajoutés un à la fois
                get().saveSelectedItems(selectedItemsWithPrice);
            }
    
        },

        saveSelectedItems: (associatedItemsWithPrices: AddToCartItem[], isUpdate: boolean = false) => {
            if(!associatedItemsWithPrices || associatedItemsWithPrices.length === 0) return;
            const pgs = [];
            associatedItemsWithPrices.forEach(packageItem => {
                (packageItem as AddToCartPackageItem).packageDateItems.forEach(dateItem => pgs.push((dateItem as AddToCartPackageDateItem).ticket.pricegroup));
            });

            useAddTicketsToCartStore.getState().setCreditCartConditionsAsSeen(pgs);

            if (isUpdate) {
                get()._updatePackageItems(get().packageId, associatedItemsWithPrices as AddToCartPackageItem[]);
            } else {
                get()._addSelectedPackageItems(associatedItemsWithPrices as AddToCartPackageItem[]);
            }
        },

        _addSelectedPackageItems: (newPackageItems: AddToCartPackageItem[]) => {
            const newSelectedTickets = getTicketsFromItems(newPackageItems);
            
            const newTicketIds = newSelectedTickets.map(t => t.ticketId);

            // Add new tickets to the list (Remove package item with existing ticket and re-add them if needed)
            const itemsToRemove = findItemsByTicketIds(get().packageItems, newTicketIds);
            const itemsToAdd = newPackageItems.map(item => ({ 
                _id: uniqueId(), 
                type: AddToCartItemType.Package, 
                packageId: item.packageId,
                ticketNames: item.ticketNames,
                packageDateItems: item.packageDateItems,
                fixedSeat: item.fixedSeat,
                sectionName: item.sectionName
             } as AddToCartPackageItem));

            // Unselect tickets that are not in the new package items
            const ticketIdsToUnselect = getTicketIdsFromItems(itemsToRemove).filter(id => !some(newTicketIds, tid => tid === id));
            if(ticketIdsToUnselect && ticketIdsToUnselect.length > 0)
                get().removeTickets(ticketIdsToUnselect, true);

            {
                useAddTicketsToCartStore.getState().clearCartWarningReport();
    
                set(produce((draft: AddPackageToCartStoreExt) => {
                    itemsToRemove.forEach(itemToRemove => {
                        // eslint-disable-next-line sonarjs/no-nested-functions, lodash/matches-prop-shorthand
                        remove(draft.packageItems, it => it._id === itemToRemove._id);
                    });
                   
                    
                    draft.packageItems.push(...itemsToAdd);
                }), false, { type: "updatePackageItems", newPackageItems });   
            }

            useAddTicketsToCartStore.getState().setPlanFocusPointFromTickets(newSelectedTickets);
        },

        _updatePackageItems: (packageId: number, newPackageItems: AddToCartPackageItem[]) => {
            // Remove all the package items
            set(produce((draft: AddPackageToCartStoreExt) => {
                const itemsToRemove = getAddToCartItemByPackageId(draft.packageItems, packageId);

                itemsToRemove.forEach(itemToRemove => {
                    // eslint-disable-next-line sonarjs/no-nested-functions
                    remove(draft.packageItems, it => it === itemToRemove);
                });

                // Add new package items to the list
                newPackageItems.forEach((item: AddToCartPackageItem) => {
                    draft.packageItems.push({ 
                        _id: uniqueId(), 
                        type: AddToCartItemType.Package,
                        packageId: item.packageId, 
                        ticketNames: item.ticketNames,
                        packageDateItems: item.packageDateItems,
                        fixedSeat: item.fixedSeat,
                        sectionName: item.sectionName
                    } as AddToCartPackageItem);
                });

            }), false, { type: "updatePackageItems/updateItems", packageId, newPackageItems });
        },

        addNewTickets: async (foundTickets: TicketModel[]): Promise<boolean> => {
            const allItems = getCartPackageItemsFromTicketModels(get().packageId, get().selectedEventDateIds, foundTickets, get().fixedSeat());

            return get()._runPriceSelectionForNewItems(allItems);
        },

        refreshPricesListPanel: async (addToCartItem: AddToCartPackageItem[]) => {
            set({ fetchingPricePanel: true }, false, "setfetchingpricepanel/true");

            //Check if there is only one price group, so we can skip the price selection modal
            const pricePanel: TicketPriceSingleSelectionPanel = await getTicketPriceSingleSelectionPanel(addToCartItem, get().getAddToCartRequest(), get().selectedEventDateIds);
            
            set({ pricePanel, fetchingPricePanel: false }, false, "setPricePanel");
        },


        confirmPriceSelectionCompleted: (addToCartItems: AddToCartItem[], pricesQty: PriceQuantity[]) => {
            useAddTicketsToCartStore.getState().confirmPriceSelection(addToCartItems, pricesQty, get().pricePanel.priceGroupsByEventDateId);
            useAddTicketsToCartStore.getState().setAutoFindTicket(null);
        },


        /** When the user clicks on a ticket to modify its price */
        startItemModification: async (itemId: string) => {
            const item = findItemById(get().packageItems, itemId);

            if (item) {
                get().refreshPricesListPanel([item] as AddToCartPackageItem[]);
                useAddTicketsToCartStore.getState().showTicketPriceSelectionModal([Object.assign({}, item)], true);
            }
        },

        /**
         * Remove one or more package item using one of their ticket id. This is usefull
         * for the venue plan that works with simple ticket ids.
         * @param ticketIds 
         */
        startItemRemovalByTicketIdsProcessWithConfirm: (ticketIds: number[]) => {
            
            if(!ticketIds || ticketIds.length === 0) return;

            // Find package item that contains the ticket ids
            const packageItemsToRemove = get().packageItems.filter(item => {
                return item.packageDateItems.some(dateItem => {
                    return some(ticketIds, id => id === dateItem.ticket.ticketId);
                });
            });

            

            // Get all ticket ids that are in the package items to remove
            const ticketIdsToRemove = packageItemsToRemove.flatMap((item) => {
                return item.packageDateItems.map(dateItem => dateItem.ticket.ticketId);
            });

            useAddTicketsToCartStore.getState().startTicketRemovalProcessWithConfirm(ticketIdsToRemove);
        },

        /**
         * Callback for when a client has selected a seat. Here, the price is not selected yet
         * and is `undefined` in the {@link AddToCartItem} object.
         * @param addToCartItem Add to cart item package. Contains the array of items for all package event dates
         * @returns `true` if selection is successful ; `false` otherwise. 
         */
        _runPriceSelectionForNewItems: async (addToCartItems: AddToCartPackageItem[]): Promise<boolean> => {

            const confirmCallback = new Promise((resolve: (bool) => void) => { 
                useAddTicketsToCartStore.setState({ addNewTicketConfirmCallback: { cb: resolve }}, false, "addNewTickets/confirmCallback" );
            });
            
            // Refresh the price panel
            await get().refreshPricesListPanel(addToCartItems);

            const pricePanel: TicketPriceSingleSelectionPanel = get().pricePanel;

            // Sélection du tarif automatique en tourvant le tarifs par son nom dans la liste de tarifs disponibles 
            const lastSelectedPriceGroup = getSelectedLastPriceGroup(useAddTicketsToCartStore.getState().lastSelectedPriceGroup, pricePanel.priceGroups);
        
            const pricesQty: PriceQuantity = { quantity: addToCartItems.length };

            // On sélection le tarif automatiquement si il n'y a qu'un seul tarif disponible
            if(lastSelectedPriceGroup) {
                pricesQty.priceGroup = lastSelectedPriceGroup;
                useAddTicketsToCartStore.setState({ticketPriceSelectionEditMode: false}, false, "setticketpriceselectioneditmode/false");
                get().confirmPriceSelectionCompleted(addToCartItems, [pricesQty]);
            }
            else{
                // On vérifie si il y a un tarif qui n'est pas désactivé
                if (pricePanel !== null && useAddTicketsToCartStore.getState().addTicketWithoutPriceSelection(pricePanel)) {
                    pricesQty.priceGroup = pricePanel.priceGroups.find(pg => !pg.Disabled);
                    get().confirmPriceSelectionCompleted(addToCartItems, [pricesQty]);
                }
                else {
                    // In here, the price is not selected yet and is undefined in the selectedTicket object
                    useAddTicketsToCartStore.getState().showTicketPriceSelectionModal(addToCartItems, false);
                }
            }
            

            // In here, the price is not selected yet and is undefined in the selectedTicket object
            useAddTicketsToCartStore.setState({ searchingTicket: false }, false, "setsearchingticket/false");

            // Return this handle to the caller.
            return confirmCallback;
            
        },

        addPackageToCart: async (): Promise<AddToCartStoreResult> => {
        
            // We probably want to validate if everything is complete here
            const allOk = areCartItemsComplete(get().packageItems);
            if(!allOk) return; // TODO: I guess we won't show error message here because we showed it before in the ui.

            // TODO This next state should be in AddTicketsToCartStore
            // _this.setState({ ticketsBeingAddedInCart: true });

            

            const addToCartRequestPackage = createAddToCartPackageRequest(get().packageId, get().packageItems, get().selectedEventDateIds);

            const serverResult = await addPackageToCartNewSale(addToCartRequestPackage);

            const result = {
                addToCartRequest: addToCartRequestPackage,
                result: serverResult,
                expiredTicketIDs: null,
                expiredTicketWarningMessage: null,
            } as AddToCartStoreResult;

            if ((!serverResult.IsSuccess && !serverResult.RedirectToInvalidOperation && !serverResult.MustRedirectToCart)
                && (serverResult.warningsReport?.generalWarnings?.length > 0 
                    || serverResult.warningsReport?.priceListWarnings?.length > 0)
            )
            {
                useAddTicketsToCartStore.getState().setAddToCartWarningReport(serverResult.warningsReport);

                let expiredTicketsWarning = getExpiredTicketsInWarningReport(serverResult.warningsReport, true);
                if(expiredTicketsWarning.expiredTicketIds.length > 0) {
                    await get().removeTickets(expiredTicketsWarning.expiredTicketIds, false);
                    result.expiredTicketIDs = expiredTicketsWarning.expiredTicketIds;
                    result.expiredTicketWarningMessage = expiredTicketsWarning.warningMessage;
                }
            }

            return result;
            
            // Version Forfait : 
            // tsapp.mvcapi.postCtl('buypackage/addticketstocartnewsale', addToCartRequestPackage)
            //         .success(async function() { 
            //         try {
            //             const trackingData = prepareAddPackageToCartTrackingData(
            //                 tsapp.currentCurrency, _this.props.packageInfo, addToCartRequestPackage, _this.state.priceList
            //             );

            //             await _this.clientTracking.trackEcommerceEvent(_this.props.integ, trackingData);
            //         } catch (e) { console.log(e); }
            //         if(_this.props.onProductAddedToCart != null) 
            //             _this.props.onProductAddedToCart(placeToGo); 
            //         }.bind(_this))
            //         .notSuccess(function(data: AddPackageTicketsToCartResult) {
            //             _this.setState({ ticketsBeingAddedInCart: false });
            //             return _this.addPackageTicketToCart_NotSuccess(data);
            //         }.bind(_this))
            //         .commError(function() {
            //             _this.setState({ ticketsBeingAddedInCart: false });

            //             return _this.addPackageTicketToCart_NotSuccess({
            //                 IsSuccess: false
            //             }, true);
            //         }.bind(_this));

            // **************************
            // Version nouvelle ajout de billet regulier au panier
            // const serverResult = await addTicketsToCartNewSale(addToCartRequest);

            // const result = {
            //     addToCartRequest,
            //     result: serverResult,
            //     expiredTicketIDs: null,
            //     expiredTicketWarningMessage: null,
            // };

            // if ((!serverResult.IsSuccess && !serverResult.RedirectToInvalidOperation && !serverResult.MustRedirectToCart)
            //     && (serverResult.warningsReport?.eventDateWarnings?.length > 0 
            //         || serverResult.warningsReport?.priceListWarnings?.length > 0
            //         || serverResult.warningsReport?.expiredTicketsWarning != null)
            // )
            // {
            //     get().setWarningsReportAfterAddToCart(serverResult.warningsReport);

            //     let expiredTicketsWarning = getExpiredTicketsInWarningReport(serverResult.warningsReport);
            //     if(expiredTicketsWarning.expiredTicketIds.length > 0) {
            //         get().removeTickets(serverResult.warningsReport.eventDateId, expiredTicketsWarning.expiredTicketIds, false);
            //         result.expiredTicketIDs = expiredTicketsWarning.expiredTicketIds;
            //         result.expiredTicketWarningMessage = expiredTicketsWarning.warningMessage;
            //     }
            // }

            // return result;
        },

        hasSelectedItems: () => {
            return get().packageItems.length > 0;
        },

        removeAllTickets: () => {
            const ticketIdsToRemove = getTicketsFromItems(get().packageItems).map(t => t.ticket.TicketID);
            useAddTicketsToCartStore.getState().startTicketRemovalProcessWithConfirm(ticketIdsToRemove);
        },

        confirmRemoveTicketsProcess: async () => {
            await get().removeTickets(useAddTicketsToCartStore.getState().ticketsAwaitingForRemoval.ticketIds, true);

            useAddTicketsToCartStore.setState({ 
                ticketsAwaitingForRemoval: null,
                showRemoveTicketsConfirmPopup: false,
                searchTicketErrorMessage: null,
            });
        },
        
        removeTickets: async (ticketIdsToRemove: number[], unselect: boolean) => {
            // In there, we add new ticekt number that we should unselect. Especially in fixed seat mode, we should unselect all the tickets in a chain.
            // unselect = false generaly means that they are already unselected.
            const ticketsToUnselect: number[] = unselect ? [...ticketIdsToRemove] : []; // If "unselect" we unselect, else, we let maybe the other tickets be unselected.

            set(produce((draft: AddPackageToCartStoreExt) => {
                // Trouver tous les items de forfait qui contiennent les billets?
                const packageItemsWithTickets = draft.packageItems.filter((packageItem) => {
                    return packageItem.packageDateItems.some((dateItem) => {
                        return some(ticketIdsToRemove, id => id === dateItem.ticket.ticketId);
                    });
                });

                // Boucler dans chacun d'eux, enlever les billets pas rapport et faire la validation que la chaine est vide ou tout vider si en mode place fixe.
                packageItemsWithTickets.forEach((packageItem) => {

                    if(get().fixedSeat()) {
                        // TODO: Sortir tous les billets de toutes les dates
                        packageItem.packageDateItems
                            .map((dateItem) => dateItem.ticket?.ticketId) // Get all ticket ids
                            .filter(id => id != null && !some(ticketIdsToRemove, idToRem => idToRem == id)) // Remove nulls and also tickets that we already have in ticketIdsToRemove because we don`t need to unselect these twice.
                            .forEach(id => ticketsToUnselect.push(id)); // Ajouter les autres billets de la chaine à la liste de ToUnselect

                        // Enlever le package item complet de la liste de package items
                        // On parle ici de place fixe, enlever une place = enlever tout le forfait en question.
                        remove(draft.packageItems, it => it === packageItem);

                    } else {
                        // Trouver la ou les dates qui contiennent les billets
                        packageItem.packageDateItems.forEach((dateItem) => {
                            if(some(ticketIdsToRemove, id => id === dateItem.ticket.ticketId))
                                dateItem.ticket = null; // Mettre à null les billet de ces date items.
                        });
                    }
                });
                
            }), false, { type: "removeTickets", ticketIdsToRemove });

            if(ticketsToUnselect.length > 0) {
                await unselectSpecificTickets(ticketsToUnselect);
            }

            useAddTicketsToCartStore.getState().clearCartWarningReport();
            useAddTicketsToCartStore.getState().forcePlanRefresh();

            // Remove the last price group to force the user to select a new one
            useAddTicketsToCartStore.setState({lastSelectedPriceGroup: null}, false, "removeTickets/lastSelectedPriceGroup");

            return true;
        },

        clearAllTickets: (withUnselect: boolean = true) => {
            set({ packageItems: [] }, false, "clearAllTickets");
            if(withUnselect)
                clearUnselectedTickets();
        },

        __recalcSubItemsByEventDateId: new StoreEffect<AddPackageToCartStoreExt>(
            s => s.packageItems, 
        (state) => {
            const subItemsByEventDateId = {};
            if(state.packageItems) {
                state.packageItems.forEach((item) => {
                    item.dates.forEach((dateItem) => {
                        if(subItemsByEventDateId[dateItem.eventDateId] == null) {
                            subItemsByEventDateId[dateItem.eventDateId] = [];
                        }
                        subItemsByEventDateId[dateItem.eventDateId].push({
                            eventDateId: dateItem.eventDateId,
                            parent: item,
                            items: dateItem
                        });
                    });
                });
            }

            set({ subItemsByEventDateId }, false, "subItemsByEventDateId");
        }),

        __recalculateSelectedTicketsByEventDateId: new StoreEffect<AddPackageToCartStoreExt>(
            state => state.packageItems
        , () => {
            const newDict: SelectedTicketsByEventDateId = {};
        
            if(get().packageItems){
                const edItems = getAllEventDateItemsFromItems(get().packageItems);
                edItems.forEach(edItem => {
                    const edId = (edItem as EventDateCartItem).eventDateId;
                    if(!newDict[edId]) 
                        newDict[edId] = {
                            eventDateId: edId,
                            selectedTickets: [],
                        };
            
                    newDict[edId].selectedTickets.push(...getTicketsFromItem(edItem));
                });
            }
        
            // console.log("Auto-updating selectedTicketsByEventDateId with ", newDict);
        
            set({
                selectedTicketsByEventDateId: newDict,
            }, false, "_recalculateSelectedTicketsByEventDateId");
            
        }),

        getAddToCartRequest: () => {
            return createAddToCartPackageRequest(get().packageId, get().packageItems, get().selectedEventDateIds);
        },

        // Price list
        applyPriceChangeSuggestions: (priceChangeSuggestions: PriceChangeSuggestion[]) => {
            if(priceChangeSuggestions && priceChangeSuggestions.length > 0) {
    
                set({ 
                    packageItems: produce(get().packageItems, draft => {
                        forEach(priceChangeSuggestions, (suggestion) => {
                            const ticket = findTicketInItemsByTicketId(draft, suggestion.ticketId);
                            if(ticket) {
                                ticket.priceId = suggestion.newPriceId;
                                ticket.pricegroup = suggestion.newPrice;
                            }
                        });  
                    }
                )});
            }
        },

        getSelectedSectionNames: () => {
            const section = useAddTicketsToCartStore.getState().selectedSection;

            if(section) 
                return [section.Name];
            else
                return null;
        },
    };
};


export const useAddPackageToCartStoreExt = connectStoreEffects(create<AddPackageToCartStoreExt>()(
    subscribeWithSelector(
        devtools(AddPackageToCartStoreExtDef,
            { 
                name: 'AddPackageToCartStoreExtDef', 
                store: 'AddPackageToCartStoreExtDef' 
            }
        )
    )
));
